import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Alert,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Ionicons } from '@expo/vector-icons';
import { RootStackParamList } from '../../types/navigation';
import { useAuth } from '../../hooks/useAuth';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button from '../../components/common/Button';
import { useTheme } from '../../hooks/useTheme';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import ResponsiveContainer from '../../components/shared/ResponsiveContainer';
import SearchBar from '../../components/shared/SearchBar';
import SupportForm from '../../components/shared/SupportForm';
import { fuzzyMatchesSearch } from '../../utils/fuzzySearch';

interface HelpCategory {
  id: string;
  title: string;
  description: string;
  icon: string;
  color: string;
  articles: HelpArticle[];
}

interface HelpArticle {
  id: string;
  title: string;
  content: string;
  tags: string[];
  isPopular?: boolean;
}

const parsePopularArticleId = (
  compositeId: string,
  categories: HelpCategory[]
): { categoryId: string; articleId: string } | null => {
  const category = categories.find((item) =>
    compositeId.startsWith(`${item.id}-`)
  );
  if (!category) return null;

  return {
    categoryId: category.id,
    articleId: compositeId.slice(category.id.length + 1),
  };
};

const HelpCenterScreen = () => {
  useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  useAuth();
  const insets = useSafeAreaInsets();
  const { colors } = useTheme();
  const { isMobile } = useResponsiveLayout();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [expandedArticle, setExpandedArticle] = useState<string | null>(null);
  const [expandedPopularId, setExpandedPopularId] = useState<string | null>(
    null
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [supportModalVisible, setSupportModalVisible] = useState(false);

  const helpCategories: HelpCategory[] = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      description: 'Learn the basics of using Coloursense',
      icon: 'rocket-outline',
      color: colors.supportText,
      articles: [
        {
          id: 'welcome-to-coloursense',
          title: 'Welcome to Coloursense',
          content:
            'Coloursense is your personal AI-powered color analysis app. Discover your perfect color palette with AI-powered analysis, virtual draping, and colour generation.\n\nFeatures:\n• AI-powered color analysis\n• Virtual draping\n• Colour Generation',
          tags: ['beginner', 'overview'],
        },
        {
          id: 'first-color-analysis',
          title: 'Your First Colour Analysis',
          content:
            "Ready to discover your perfect colors? Here's how to get started:\n\n1. Navigate to Colour Analysis in the main menu\n2. Take a photo in good lighting (natural light is best)\n3. Ensure your face is clearly visible\n4. Wait for AI to analyze your features\n5. Review your personalized color palette\n6. Save your results for future reference\n\nTips for best results:\n• Use natural lighting\n• Remove makeup if possible\n• Take multiple photos from different angles\n• Ensure good image quality",
          tags: ['color-analysis', 'tutorial', 'beginner'],
          isPopular: true,
        },
        {
          id: 'understanding-results',
          title: 'Understanding Your Results',
          content:
            'Your color analysis results include:\n\n• Primary Colour Palette: Your best colors\n• Secondary Palette: Good alternatives\n• Seasonal Type: Your seasonal color category\n• Undertone: Warm, cool, or neutral\n• Recommendations: Specific colors for clothing, makeup, and accessories\n\nEach color in your palette is carefully selected based on your unique features, skin tone, hair color, and eye color.',
          tags: ['results', 'interpretation', 'beginner'],
        },
      ],
    },
    {
      id: 'color-analysis',
      title: 'Colour Analysis',
      description: 'Master the art of color analysis',
      icon: 'color-palette-outline',
      color: colors.theme_color,
      articles: [
        {
          id: 'how-analysis-works',
          title: 'How AI Colour Analysis Works',
          content:
            'Our AI analyzes multiple factors to determine your perfect colors:\n\n• Skin Undertone: Warm, cool, or neutral\n• Skin Surface Tone: Your visible skin color\n• Hair Color: Natural or dyed\n• Eye Color: Including patterns and intensity\n• Overall Contrast: High, medium, or low\n\nThe AI then matches these characteristics to established color theory principles to create your personalized palette.',
          tags: ['ai', 'technology', 'how-it-works'],
          isPopular: true,
        },
        {
          id: 'best-photos',
          title: 'Taking the Best Photos',
          content:
            'Photo quality significantly impacts analysis accuracy:\n\nLighting:\n• Natural daylight is ideal\n• Avoid harsh overhead lighting\n• No direct sunlight on face\n• Consistent lighting across photos\n\nSetup:\n• Plain, neutral background\n• No colored clothing near face\n• Remove glasses if possible\n• Natural expression\n• Multiple angles (front, 3/4 view)\n\nCamera:\n• High resolution (at least 1MP)\n• Good focus on face\n• No filters or editing',
          tags: ['photography', 'tips', 'accuracy'],
        },
        {
          id: 'interpreting-palette',
          title: 'Interpreting Your Colour Palette',
          content:
            'Your color palette is organized by:\n\nPrimary Colors: Your absolute best colors\n• These will make you look most vibrant\n• Use for important pieces and special occasions\n\nSecondary Colors: Good alternatives\n• These complement your features well\n• Great for everyday wear\n\nAccent Colors: Statement pieces\n• Use sparingly for impact\n• Perfect for accessories and details\n\nUnderstanding these categories helps you make better wardrobe decisions.',
          tags: ['palette', 'interpretation', 'wardrobe'],
        },
      ],
    },
    // {
    //   id: "wardrobe",
    //   title: "Smart Wardrobe",
    //   description: "Build and manage your digital wardrobe",
    //   icon: "shirt-outline",
    //   color: colors.success,
    //   articles: [
    //     {
    //       id: "adding-items",
    //       title: "Adding Items to Your Wardrobe",
    //       content:
    //         "Build your digital wardrobe step by step:\n\n1. Navigate to Smart Wardrobe\n2. Tap the '+' button to add an item\n3. Take a photo or upload from gallery\n4. Fill in item details:\n   • Category (top, bottom, dress, etc.)\n   • Color\n   • Season\n   • Occasion\n5. Save the item\n\nTips:\n• Take photos in good lighting\n• Include the full item\n• Be specific with colors\n• Add tags for easy searching",
    //       tags: ["wardrobe", "tutorial", "organization"],
    //     },
    //     {
    //       id: "wardrobe-organization",
    //       title: "Organizing Your Wardrobe",
    //       content:
    //         "Keep your digital wardrobe organized:\n\nCategories:\n• Tops: Shirts, blouses, sweaters\n• Bottoms: Pants, skirts, shorts\n• Dresses: All types of dresses\n• Outerwear: Jackets, coats, blazers\n• Accessories: Bags, jewelry, scarves\n\nTags:\n• Season: Spring, Summer, Fall, Winter\n• Occasion: Casual, Work, Formal, Sport\n• Color: Match to your palette\n• Style: Classic, Trendy, Bohemian\n\nUse the search and filter functions to find items quickly.",
    //       tags: ["organization", "categories", "search"],
    //     },
    //     {
    //       id: "outfit-suggestions",
    //       title: "Getting Outfit Suggestions",
    //       content:
    //         "AI-powered outfit suggestions based on your color palette:\n\nHow it works:\n• AI analyzes your wardrobe items\n• Matches them to your color palette\n• Considers weather and occasion\n• Suggests complete outfits\n\nTo get suggestions:\n1. Go to 'Wear This Today'\n2. Select your preferences\n3. Choose occasion and weather\n4. Get personalized outfit recommendations\n\nYou can save favorite combinations for future reference.",
    //       tags: ["outfits", "ai", "recommendations"],
    //       isPopular: true,
    //     },
    //   ],
    // },
    {
      id: 'virtual-drape',
      title: 'Virtual Drape Test',
      description: 'Test colors virtually before buying',
      icon: 'camera-outline',
      color: colors.helpCenterText,
      articles: [
        {
          id: 'drape-testing',
          title: 'How Virtual Drape Testing Works',
          content:
            'Virtual drape testing lets you see how colors look on you:\n\n1. Take a photo or use existing one\n2. Select colors from your palette\n3. AI applies colors virtually to your image\n4. See how the color affects your appearance\n5. Compare multiple colors side by side\n\nBenefits:\n• Test colors before purchasing\n• See how colors work with your features\n• Compare different shades\n• Build confidence in color choices\n• Save money on wrong purchases',
          tags: ['virtual-drape', 'testing', 'colors'],
          isPopular: true,
        },
        {
          id: 'drape-tips',
          title: 'Tips for Virtual Drape Testing',
          content:
            'Get the most accurate results:\n\nPhoto Quality:\n• Use the same photo from your analysis\n• Ensure good lighting and clarity\n• Face should be clearly visible\n• Neutral expression works best\n\nTesting Strategy:\n• Start with your primary palette colors\n• Test one color at a time\n• Compare similar shades\n• Consider different lighting conditions\n• Save your favorite results',
          tags: ['tips', 'accuracy', 'testing'],
        },
      ],
    },
    // {
    //   id: "mood-boards",
    //   title: "Mood Boards",
    //   description: "Create and share color inspiration",
    //   icon: "grid-outline",
    //   color: colors.billing,
    //   articles: [
    //     {
    //       id: "creating-mood-boards",
    //       title: "Creating Your First Mood Board",
    //       content:
    //         "Mood boards help you visualize color combinations:\n\n1. Navigate to Mood Boards\n2. Tap 'Create New Board'\n3. Choose a theme or occasion\n4. Add colors from your palette\n5. Include inspiration images\n6. Add notes and ideas\n7. Save and share\n\nUses:\n• Plan outfits for special occasions\n• Organize seasonal wardrobes\n• Share ideas with stylists\n• Track style evolution\n• Inspire future purchases",
    //       tags: ["mood-boards", "creation", "inspiration"],
    //     },
    //     {
    //       id: "sharing-boards",
    //       title: "Sharing and Collaborating",
    //       content:
    //         "Share your mood boards with others:\n\nSharing Options:\n• Private: Only you can see\n• Public: Anyone can view\n• Shared: Specific people you invite\n\nCollaboration Features:\n• Add comments and notes\n• Invite friends or stylists\n• Get feedback on combinations\n• Share shopping lists\n• Create group boards\n\nPrivacy controls let you choose what to share.",
    //       tags: ["sharing", "collaboration", "social"],
    //     },
    //   ],
    // },
    // {
    //   id: "subscription",
    //   title: "Subscription & Billing",
    //   description: "Manage your subscription and billing",
    //   icon: "card-outline",
    //   color: colors.subscriptionAndBillingIcon,
    //   articles: [
    //     {
    //       id: "subscription-plans",
    //       title: "Understanding Subscription Plans",
    //       content:
    //         "Choose the plan that works for you:\n\nFree Plan:\n• 5 color analysis results\n• Basic mood boards\n• Limited wardrobe items\n• Community access (limited)\n\nPro Plan ($9.99/month):\n• Unlimited color analysis\n• Unlimited mood boards\n• Full wardrobe access\n• AI outfit suggestions\n• Priority support\n• Early access features\n\nPay-per-scan ($2.99):\n• One-time deep analysis\n• Detailed color report\n• Personalized recommendations",
    //       tags: ["subscription", "plans", "pricing"],
    //       isPopular: true,
    //     },
    //     {
    //       id: "managing-subscription",
    //       title: "Managing Your Subscription",
    //       content:
    //         "Easy subscription management:\n\nView Current Plan:\n• Go to Settings > Billing & Subscription\n• See your current plan and features\n• Check billing cycle and next payment\n\nChange Plans:\n• Upgrade or downgrade anytime\n• Start free trial\n• Cancel subscription\n• Update payment method\n\nBilling Support:\n• Contact support for issues\n• Request refunds\n• Get billing history\n• Update payment information",
    //       tags: ["management", "billing", "support"],
    //     },
    //   ],
    // },
    {
      id: 'troubleshooting',
      title: 'Troubleshooting',
      description: 'Solve common issues and problems',
      icon: 'construct-outline',
      color: colors.secondary,
      articles: [
        {
          id: 'analysis-issues',
          title: 'Colour Analysis Not Working',
          content:
            "If your color analysis isn't working properly:\n\nCommon Issues:\n• Poor lighting in photos\n• Face not clearly visible\n• Low image quality\n• Network connection problems\n\nSolutions:\n• Retake photos in better lighting\n• Ensure face is clearly visible\n• Check internet connection\n• Try different angles\n• Contact support if issues persist\n\nFor technical problems, our support team is here to help.",
          tags: ['troubleshooting', 'analysis', 'technical'],
        },
        {
          id: 'app-performance',
          title: 'App Performance Issues',
          content:
            'Improve app performance:\n\nStorage:\n• Clear cache regularly\n• Delete unused photos\n• Free up device storage\n\nNetwork:\n• Use stable internet connection\n• Try Wi-Fi instead of mobile data\n• Check for app updates\n\nDevice:\n• Restart the app\n• Update your device OS\n• Close other apps\n• Restart your device\n\nIf problems continue, contact support.',
          tags: ['performance', 'technical', 'optimization'],
        },
      ],
    },
  ];

  const handleCategorySelect = (categoryId: string) => {
    if (selectedCategory === categoryId) {
      setSelectedCategory(null);
      setExpandedArticle(null);
      return;
    }

    setSelectedCategory(categoryId);
    setExpandedArticle(null);
    setExpandedPopularId(null);
  };

  const handleArticleToggle = (articleId: string) => {
    setExpandedArticle(expandedArticle === articleId ? null : articleId);
    setExpandedPopularId(null);
  };

  const handlePopularArticlePress = (compositeId: string) => {
    const parsed = parsePopularArticleId(compositeId, helpCategories);
    if (!parsed) return;

    const { categoryId, articleId } = parsed;
    const isClosing =
      expandedPopularId === compositeId &&
      selectedCategory === categoryId &&
      expandedArticle === articleId;

    if (isClosing) {
      setExpandedPopularId(null);
      setSelectedCategory(null);
      setExpandedArticle(null);
      return;
    }

    setExpandedPopularId(compositeId);
    setSelectedCategory(categoryId);
    setExpandedArticle(articleId);
  };

  const normalizedSearchQuery = searchQuery.trim();

  const articleMatchesSearch = (article: HelpArticle) =>
    fuzzyMatchesSearch(
      normalizedSearchQuery,
      article.title,
      article.content,
      article.tags.join(' ')
    );

  const categoryMatchesSearch = (category: HelpCategory) =>
    fuzzyMatchesSearch(
      normalizedSearchQuery,
      category.title,
      category.description
    ) || getMatchingArticles(category).length > 0;

  const getMatchingArticles = (category: HelpCategory) => {
    if (!normalizedSearchQuery) return category.articles;
    return category.articles.filter(articleMatchesSearch);
  };

  const getFilteredCategories = () => {
    if (!normalizedSearchQuery) return helpCategories;
    return helpCategories.filter(categoryMatchesSearch);
  };

  const getPopularArticles = () => {
    const popular: HelpArticle[] = [];
    helpCategories.forEach((category) => {
      category.articles.forEach((article) => {
        if (article.isPopular) {
          popular.push({ ...article, id: `${category.id}-${article.id}` });
        }
      });
    });
    return popular;
  };

  const getFilteredPopularArticles = () => {
    if (!normalizedSearchQuery) return getPopularArticles();
    return getPopularArticles().filter((article) =>
      articleMatchesSearch(article)
    );
  };

  const renderArticleContent = (article: HelpArticle) => (
    <View
      style={[styles.articleContent, { borderTopColor: colors.borderLight }]}
    >
      <Text style={[styles.articleText, { color: colors.text }]}>
        {article.content}
      </Text>
      <View style={styles.articleTags}>
        {article.tags.map((tag, index) => (
          <View
            key={index}
            style={[styles.tag, { backgroundColor: colors.border }]}
          >
            <Text style={[styles.tagText, { color: colors.secondary }]}>
              {tag}
            </Text>
          </View>
        ))}
      </View>
    </View>
  );

  const renderCategory = (category: HelpCategory) => {
    const matchingArticles = getMatchingArticles(category);
    const isExpanded = selectedCategory === category.id;

    return (
      <View
        key={category.id}
        style={[
          styles.categoryCard,
          { backgroundColor: colors.card, borderColor: colors.border },
        ]}
      >
        <TouchableOpacity
          style={styles.categoryHeader}
          onPress={() => handleCategorySelect(category.id)}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityState={{ expanded: isExpanded }}
        >
          <View style={styles.categoryHeaderContent}>
            <View
              style={[styles.categoryIcon, { backgroundColor: category.color }]}
            >
              <Ionicons
                name={category.icon as keyof typeof Ionicons.glyphMap}
                size={24}
                color={colors.background}
              />
            </View>
            <View style={styles.categoryInfo}>
              <Text style={[styles.categoryTitle, { color: colors.text }]}>
                {category.title}
              </Text>
              <Text
                style={[
                  styles.categoryDescription,
                  { color: colors.secondary },
                ]}
              >
                {category.description}
              </Text>
            </View>
          </View>
          <Ionicons
            name={isExpanded ? 'chevron-up' : 'chevron-down'}
            size={20}
            color={colors.secondary}
          />
        </TouchableOpacity>

        {isExpanded && (
          <View
            style={[
              styles.articlesContainer,
              { borderTopColor: colors.borderLight },
            ]}
          >
            {matchingArticles.map((article) => (
              <View
                key={article.id}
                style={[styles.articleItem, { borderColor: colors.border }]}
              >
                <TouchableOpacity
                  style={styles.articleHeader}
                  onPress={() => handleArticleToggle(article.id)}
                  activeOpacity={0.7}
                  accessibilityRole="button"
                  accessibilityState={{
                    expanded: expandedArticle === article.id,
                  }}
                >
                  <View style={styles.articleHeaderContent}>
                    {article.isPopular && (
                      <View
                        style={[
                          styles.popularBadge,
                          { backgroundColor: colors.helpCenterText },
                        ]}
                      >
                        <Text
                          style={[
                            styles.popularText,
                            { color: colors.background },
                          ]}
                        >
                          Popular
                        </Text>
                      </View>
                    )}
                    <Text style={[styles.articleTitle, { color: colors.text }]}>
                      {article.title}
                    </Text>
                  </View>
                  <Ionicons
                    name={
                      expandedArticle === article.id
                        ? 'chevron-up'
                        : 'chevron-down'
                    }
                    size={16}
                    color={colors.secondary}
                  />
                </TouchableOpacity>

                {expandedArticle === article.id &&
                  renderArticleContent(article)}
              </View>
            ))}
          </View>
        )}
      </View>
    );
  };

  const filteredCategories = getFilteredCategories();
  const filteredPopularArticles = getFilteredPopularArticles();

  const searchSection = (
    <View style={styles.section}>
      <Text style={[styles.sectionTitle, { color: colors.primary }]}>
        Search Help Articles
      </Text>
      <SearchBar
        value={searchQuery}
        onChangeText={setSearchQuery}
        placeholder="Search for topics, features, or questions..."
        containerStyle={styles.searchBarContainer}
        style={{ backgroundColor: colors.card }}
        size="wide"
      />
    </View>
  );

  const popularSection = (
    <View style={styles.section}>
      <Text style={[styles.sectionTitle, { color: colors.primary }]}>
        Popular Articles
      </Text>
      <View style={styles.popularContainer}>
        {filteredPopularArticles.length > 0 ? (
          filteredPopularArticles.map((article) => {
            const isExpanded = expandedPopularId === article.id;

            return (
              <View
                key={article.id}
                style={[
                  styles.popularArticleCard,
                  { backgroundColor: colors.card, borderColor: colors.border },
                ]}
              >
                <TouchableOpacity
                  style={styles.popularArticleHeader}
                  onPress={() => handlePopularArticlePress(article.id)}
                  activeOpacity={0.7}
                  accessibilityRole="button"
                  accessibilityState={{ expanded: isExpanded }}
                >
                  <View style={styles.popularArticleContent}>
                    <Text
                      style={[
                        styles.popularArticleTitle,
                        { color: colors.text },
                      ]}
                    >
                      {article.title}
                    </Text>
                    {!isExpanded && (
                      <Text
                        style={[
                          styles.popularArticlePreview,
                          { color: colors.secondary },
                        ]}
                      >
                        {article.content.substring(0, 100)}...
                      </Text>
                    )}
                  </View>
                  <Ionicons
                    name={isExpanded ? 'chevron-up' : 'chevron-down'}
                    size={16}
                    color={colors.secondary}
                  />
                </TouchableOpacity>
                {isExpanded && renderArticleContent(article)}
              </View>
            );
          })
        ) : normalizedSearchQuery.length >= 2 ? (
          <Text style={[styles.noResultsText, { color: colors.secondary }]}>
            No popular articles match &quot;{normalizedSearchQuery}&quot;.
          </Text>
        ) : null}
      </View>
    </View>
  );

  return (
    <ResponsiveContainer style={{ backgroundColor: colors.border }}>
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={{
          paddingBottom: 40 + insets.bottom,
          paddingHorizontal: 16,
          paddingTop: 16,
        }}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        {/* Header */}
        <View style={styles.header}>
          <Text style={[styles.title, { color: colors.text }]}>
            Help Center
          </Text>
          <Text style={[styles.subtitle, { color: colors.secondary }]}>
            Find answers to your questions and learn how to use Coloursense
          </Text>
        </View>

        {isMobile ? (
          <>
            {searchSection}
            {popularSection}
          </>
        ) : (
          <>
            {popularSection}
            {searchSection}
          </>
        )}

        {/* Categories */}
        <View style={styles.section}>
          <Text style={[styles.sectionTitle, { color: colors.primary }]}>
            Browse by Category
          </Text>
          <View style={styles.categoriesContainer}>
            {filteredCategories.length > 0 ? (
              filteredCategories.map(renderCategory)
            ) : normalizedSearchQuery.length >= 2 ? (
              <Text style={[styles.noResultsText, { color: colors.secondary }]}>
                No help articles match &quot;{normalizedSearchQuery}&quot;. Try
                a different keyword.
              </Text>
            ) : null}
          </View>
        </View>

        {/* Contact Support */}
        <View
          style={[
            styles.contactCard,
            { backgroundColor: colors.card, borderColor: colors.border },
          ]}
        >
          <Text style={[styles.contactTitle, { color: colors.text }]}>
            Still Need Help?
          </Text>
          <Text style={[styles.contactText, { color: colors.secondary }]}>
            Can&apos;t find what you&apos;re looking for? Our support team is
            here to help.
          </Text>
          <Button
            title="Contact Support"
            onPress={() => setSupportModalVisible(true)}
            style={styles.contactButton}
            trackClick={true}
            trackingLocation="help-center-screen"
            trackingData={{
              button_name: 'Contact Support',
              button_type: 'contact_support',
              screen: 'help_center',
            }}
          />
        </View>
      </ScrollView>

      <SupportForm
        visible={supportModalVisible}
        onClose={() => setSupportModalVisible(false)}
        onSuccess={() => {
          Alert.alert(
            'Success',
            'Your support request has been submitted. We will get back to you within 24 hours.'
          );
          setSupportModalVisible(false);
        }}
      />
    </ResponsiveContainer>
  );
};

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  header: {
    alignItems: 'center',
    marginBottom: 18,
  },
  title: {
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 8,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 16,
    textAlign: 'center',
    lineHeight: 24,
  },
  section: {
    marginBottom: 18,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 12,
  },
  popularContainer: {
    gap: 12,
  },
  popularArticleCard: {
    borderRadius: 12,
    borderWidth: 1,
    overflow: 'hidden',
  },
  popularArticleHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    minHeight: 44,
  },
  popularArticleContent: {
    flex: 1,
  },
  popularArticleTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4,
  },
  popularArticlePreview: {
    fontSize: 14,
    lineHeight: 20,
  },
  searchBarContainer: {
    padding: 0,
  },
  noResultsText: {
    fontSize: 14,
    lineHeight: 20,
    textAlign: 'center',
    paddingVertical: 8,
  },
  categoriesContainer: {
    gap: 16,
  },
  categoryCard: {
    borderRadius: 12,
    borderWidth: 1,
    overflow: 'hidden',
  },
  categoryHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    minHeight: 44,
  },
  categoryHeaderContent: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  categoryIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  categoryInfo: {
    flex: 1,
  },
  categoryTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 4,
  },
  categoryDescription: {
    fontSize: 14,
    lineHeight: 20,
  },
  articlesContainer: {
    borderTopWidth: 1,
  },
  articleItem: {
    borderBottomWidth: 1,
  },
  articleHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    minHeight: 44,
  },
  articleHeaderContent: {
    flex: 1,
  },
  popularBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
    alignSelf: 'flex-start',
    marginBottom: 8,
  },
  popularText: {
    fontSize: 12,
    fontWeight: '600',
  },
  articleTitle: {
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 22,
  },
  articleContent: {
    paddingHorizontal: 16,
    paddingBottom: 16,
    borderTopWidth: 1,
  },
  articleText: {
    fontSize: 15,
    lineHeight: 24,
    marginBottom: 12,
  },
  articleTags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  tag: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
  },
  tagText: {
    fontSize: 12,
    fontWeight: '500',
  },
  contactCard: {
    padding: 20,
    borderRadius: 12,
    borderWidth: 1,
    alignItems: 'center',
  },
  contactTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 8,
  },
  contactText: {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 16,
    textAlign: 'center',
  },
  contactButton: {
    minWidth: 200,
  },
});

export default HelpCenterScreen;
